import '@/common/sass/tailwind.css';
import '@/common/sass/reset.scss';
import 'react-resizable/css/styles.css';
import '@/common/sass/react-resizable.css';
import { createRoot } from 'react-dom/client';
import axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { StyleProvider, legacyLogicalPropertiesTransformer } from '@ant-design/cssinjs';
import zhCN from 'antd/es/locale/zh_CN';
import enUS from 'antd/es/locale/en_US';
import { ConfigProvider } from 'antd';
import { Provider } from 'react-redux';
import { I18nextProvider } from 'react-i18next';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import localeData from 'dayjs/plugin/localeData';
import weekday from 'dayjs/plugin/weekday';
import Pages from './pages/index';
import { isLanguageEn } from './common/utils/language';
import { isDebug } from './common/utils/env';
import { setIsOnline } from './common/store/network/slice';
import { TVersion } from './common/basis/types/tversion';
import { customAntdTheme } from './common/utils/antd';
import { getCookieUserToken } from './common/utils/cookies';
import store from '@/common/store/index';
import i18n from '@/common/basis/i18n/index';
import { logger } from '@/common/utils/logger';
import 'dayjs/locale/zh-cn';

// 初始化日期配置
dayjs.extend(duration);
dayjs.extend(weekday);
dayjs.extend(localeData);
console.log('dayjs isLanguageEn:', isLanguageEn);
isLanguageEn ? dayjs.locale('en') : dayjs.locale('zh-cn');
console.log('dayjs locale:', dayjs.locale());
isLanguageEn ? i18n.changeLanguage('en') : i18n.changeLanguage('zh'); // 初始化i18n

// 初始化axios
axios.defaults.timeout = 10000; // 请求超时时间
axios.defaults.headers.post['Content-Type'] = 'application/json';
axios.defaults.headers['Accept-Language'] = isLanguageEn ? 'en' : 'zh';
axios.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const loginToken = getCookieUserToken();

  const method = config.method;
  const params = config.params;
  const data = config.data;
  const url = config.url;

  config.headers['Authorization'] = `Bearer ${loginToken}`;
  if (isDebug) {
    logger().info('*********************api request start*********************');
    logger().info(`api-request-url: ${url}`);
    logger().info(`api-request-method: ${method}`);
    logger().info('api-request-params:', params);
    logger().info('api-request-data:', data);
    logger().info('*********************api request end***********************');
    logger().info('\n');
  }

  return { ...config };
});
axios.interceptors.response.use(
  (resp: AxiosResponse) => {
    const data = resp.data;
    const status = resp.status;
    const config = resp.config;
    const method = config.method;
    const url = config.url;

    if (isDebug) {
      logger().info('*********************api response start*********************');
      logger().info(`api-response-url: ${url}`);
      logger().info(`api-response-method: ${method}`);
      logger().info('api-response-status:', status);
      logger().info('api-response-data:', data);
      logger().info('*********************api response end*********************');
      logger().info('\n');
    }

    if (status === 200) {
      return data;
    }

    return resp;
  },
  (error) => {
    logger().error('axios error:', error);

    return Promise.reject(error);
  },
);

// 注册全局网络状态
window.addEventListener('online', () => store.dispatch(setIsOnline(true)));
window.addEventListener('offline', () => store.dispatch(setIsOnline(false)));

// 勿删 打印版本信息
logger().info('versionName:', TVersion.VERSION_NAME);
logger().info('gitCommit:', TVersion.GIT_COMMIT);
logger().info('branchName:', TVersion.BRANCH_NAME);
logger().info('buildTime:', TVersion.BUILD_TIME);

createRoot(document.getElementById('app') ?? document.body).render(
  <I18nextProvider i18n={i18n}>
    <StyleProvider hashPriority='high' transformers={[legacyLogicalPropertiesTransformer]}>
      <ConfigProvider locale={isLanguageEn ? enUS : zhCN} theme={customAntdTheme}>
        <Provider store={store}>
          <Pages />
        </Provider>
      </ConfigProvider>
    </StyleProvider>
  </I18nextProvider>,
);
